/* eslint-disable react-hooks/exhaustive-deps */
'use client'

import { ClipboardEvent, FC, FormEvent, useMemo } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'

import { METHOD_2FA_ENUM } from '@/common/constants'
import { Button, Input } from '@/components/commons'
import { yupResolver } from '@hookform/resolvers/yup'

import { FORM_CASE_ENUM } from '../../utils/constants'
import CountdownTimer from '../CountdownTimer'
import styles from './styles.module.css'
import { FormProps, ICodeFormProps } from './types'
import { schemaResetPassword } from './utils/validations'

const CodeForm: FC<ICodeFormProps> = ({
  className,
  handleConfirmationCode,
  sendButtonLoading,
  setFormCase,
  onResend,
  haveACode,
  contacts,
  currentMethod,
  setIsOpenModal,
}) => {
  const {
    register,
    handleSubmit,
    setFocus,
    setValue,
    formState: { errors },
  } = useForm<FormProps>({
    mode: 'all',
    resolver: yupResolver(schemaResetPassword),
  })

  const codeInputList = useMemo(() => {
    return ['first', 'second', 'third', 'fourth', 'fifth', 'sixth']
  }, [])

  const { contact } = useMemo(() => {
    const contactSerialized = {
      contact:
        currentMethod === METHOD_2FA_ENUM.EMAIL
          ? (contacts?.email ?? '-')
          : (contacts?.celPhoneNumber ?? '-'),
    }
    return { contact: contactSerialized.contact }
  }, [])

  function handleInputChange(
    event: FormEvent<HTMLInputElement>,
    next?: keyof FormProps,
    prev?: keyof FormProps,
  ) {
    const value = event.currentTarget.value

    if (value?.length === 1 && next) {
      setFocus(next)
    } else if (value?.length === 0 && prev) {
      setFocus(prev)
    }
  }

  function handlePaste(event: ClipboardEvent<HTMLInputElement>) {
    event.preventDefault()
    const pasteData = event.clipboardData.getData('text')
    const chars = pasteData.slice(0, 6).split('')

    codeInputList.forEach((field, index, array) => {
      setValue(field as keyof FormProps, chars[index] || '')
      if (chars[index]) {
        setFocus(array[index + 1] as keyof FormProps)
      }
    })
  }

  const onSubmit: SubmitHandler<FormProps> = async (data) => {
    const { first, second, third, fourth, fifth, sixth } = data

    handleConfirmationCode(`${first}${second}${third}${fourth}${fifth}${sixth}`)
  }

  function handleChangeMethod() {
    setFormCase(FORM_CASE_ENUM.CONTACT_FORM)
  }

  function handleCloseModal() {
    setIsOpenModal((prev) => !prev)
  }

  return (
    <form
      className={`flex flex-col w-full bg-white gap-4 ${className}`}
      onSubmit={handleSubmit(onSubmit)}
    >
      <div className="flex flex-col w-full gap-2">
        <div className="flex flex-col w-full mb-4">
          <p className="prose-body-large text-primary-900 flex-1">
            Insira o código enviado para:
          </p>
          <span className="prose-body text-primary-900 flex-1">{contact}</span>
        </div>
        <div className="flex w-full gap-1 justify-between">
          {codeInputList.map((field, index, array) => (
            <Input
              key={field}
              {...register(field as keyof FormProps)}
              className={styles.wrapperInput}
              maxLength={1}
              onChange={(e) =>
                handleInputChange(
                  e,
                  array[index + 1] as keyof FormProps,
                  array[index - 1] as keyof FormProps,
                )
              }
              onPaste={handlePaste}
              error={!!errors[field as keyof FormProps]?.message}
            />
          ))}
        </div>

        {!haveACode && <CountdownTimer onResend={onResend} />}
      </div>

      <Button
        type="submit"
        text="Confirmar"
        className="w-full"
        isLoading={sendButtonLoading}
      />
      <div className="flex w-full justify-between gap-4">
        {[
          { text: 'Cancelar', onClick: handleCloseModal },
          {
            text: 'Voltar',
            onClick: handleChangeMethod,
          },
        ].map(({ text, onClick }) => (
          <p
            key={text}
            onClick={onClick}
            className="prose-body-small self-end text-grayscale-600 hover:underline cursor-pointer active:scale-95"
          >
            {text}
          </p>
        ))}
      </div>
    </form>
  )
}

export default CodeForm
